import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponseHeaders } from 'axios'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestStatusEnum } from '@model/ClientTestAnswerDto'
import { ClientTestSubcategoryEnum } from '@model/ClientTestSubcategoryEnum'
import { CreateClientDocuments } from '@model/CreateClientDocuments'
import { CreateClientTest } from '@model/CreateClientTest'
import { DocumentPropertyType } from '@model/DocumentPropertyType'
import { UploadDocumentCategoryEnum } from '@model/UploadDocumentCategoryEnum'
import { UploadDocumentTypeEnum } from '@model/UploadDocumentTypeEnum'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { InformationModal } from '@shared/modals/Information.modal'

import { DeclinedClassificationTest } from '../DeclinedClassificationTest'
import { PendingClassificationTest } from '../PendingClassificationTest'
import {
  ProfessionalClassificationTestForm,
  ProfessionalClassificationTestValues,
} from './ProfessionalClassificationTestForm'

interface Props {
  handleOkOnClick: () => void
}

export const ProfessionalClassificationTest: React.FC<Props> = ({ handleOkOnClick }) => {
  const { product } = useProductReadContext()

  const { t } = useTranslation()

  const alreadyUploadedModal = useModal(AlreadyUploadedModal)
  const applicationInfoModal = useModal(InformationModal, {
    title: t('Profile.Application received'),
    description: t('Profile.We have received your application to change classification.'),
  })
  const tradeSizeInfoModal = useModal(InformationModal, {
    title: t('Profile.A significant sized trade'),
    secondaryLabel: t('Got It'),
    description: t(
      'Profile.A significant sized trade is equal to $/Euro/£ 40,000 notional (e.g. 0.4 of a lot of EURUSD)'
    ),
  })
  const portfolioInfoModal = useModal(InformationModal, {
    title: t('Profile.Financial Investment Portfolio'),
    secondaryLabel: t('Got It'),
    description: () => (
      <Box flex direction='column' gap='x8'>
        <span>{t('Profile.Examples of Investment Portfolios we can accept')}</span>
        <span>{t('Profile.Examples of Investment Portfolios we can not accept when')}</span>
      </Box>
    ),
  })

  const handleTradeSizeModalOpen = () => tradeSizeInfoModal.open()
  const handlePortfolioModalOpen = () => portfolioInfoModal.open()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()

  const testSubCategoryId = ClientTestSubcategoryEnum.ClassificationProfessional

  const { data: testAnswers, isLoading: isTestAnswersLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTestAnswers(testSubCategoryId, locale),
    queryFn: () =>
      apiClient.getClientClassificationTestAnswers({
        subCategoryId: testSubCategoryId,
        languageId: locale,
      }),
  })

  const { data: docs, isLoading: isDocumentLoading } = useQuery({
    queryKey: queryKeys.profile.legalDocuments(locale),
    queryFn: () => apiClient.getLegalDocuments(locale),
  })

  const { data: testData, isLoading: isTestLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTest(testSubCategoryId, locale),
    queryFn: () =>
      apiClient.getClassificationTest({
        subCategoryId: testSubCategoryId,
        languageId: locale,
      }),
  })

  const { data: isFormEnabled, isLoading: isTestAvailabilityLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTestRetake(testSubCategoryId),
    queryFn: () => apiClient.isRetakeClassificationTestEnabled(testSubCategoryId),
  })

  const { account } = useAccountReadContext()

  const preparePayload = (values: ProfessionalClassificationTestValues): CreateClientTest => {
    return {
      category: 'classification',
      clientId: account?.id || '',
      testId: testData?.id || '',
      dateTaken: new Date().toISOString(),
      selectedAnswers: values.selectedAnswers.map((a) => a.answer),
      freeAnswers: values.freeAnswers,
    }
  }

  const getTestId = (
    headers:
      | AxiosResponseHeaders
      | Partial<
          Record<string, string> & {
            'set-cookie'?: string[] | undefined
          }
        >
  ) => {
    const parts = headers.location?.split('/')
    if (parts) {
      return parts[parts.length - 1]
    }
    return ''
  }

  const prepareUploadPayload = (
    values: ProfessionalClassificationTestValues,
    testId: string
  ): CreateClientDocuments => {
    const docs: CreateClientDocuments = {
      documents: [],
    }

    values.significantTradeSize === '1' &&
      values.tradeSizeProofs.map((proof) =>
        docs.documents.push({
          categoryId: UploadDocumentCategoryEnum.Additional,
          typeId: UploadDocumentTypeEnum.Client_Classification,
          file: proof.base64Content,
          filename: proof.fileName,
          properties: {
            [DocumentPropertyType.ClientTestId]: testId,
          },
        })
      )

    values.significantPortfolio === '1' &&
      values.portfolioProofs.map((proof) =>
        docs.documents.push({
          categoryId: UploadDocumentCategoryEnum.Additional,
          typeId: UploadDocumentTypeEnum.Client_Classification,
          file: proof.base64Content,
          filename: proof.fileName,
          properties: {
            [DocumentPropertyType.ClientTestId]: testId,
          },
        })
      )

    return docs
  }

  const uploadDocs = async (values: ProfessionalClassificationTestValues, testId: string) => {
    const payload = prepareUploadPayload(values, testId)
    try {
      await apiClient.uploadDocuments(payload)
    } catch {
      alreadyUploadedModal.open()
    }
  }

  const submitForm = async (values: ProfessionalClassificationTestValues) => {
    try {
      const payload = preparePayload(values)
      if (payload) {
        const response = await apiClient.addClientClassificationTest(payload)
        await queryClient.invalidateQueries({ queryKey: ['profile'] })
        const testId = getTestId(response.headers)
        await uploadDocs(values, testId)
        applicationInfoModal.open({
          onSecondaryClick: () => {
            applicationInfoModal.close()
            handleOkOnClick()
          },
        })
      }
    } catch (error: unknown) {
      console.error(error)
    }
  }

  if (testAnswers?.testStatus.id === TestStatusEnum.Pending) {
    return <PendingClassificationTest />
  } else if (testAnswers?.testStatus.id === TestStatusEnum.Declined && !testAnswers.allowRetake) {
    return <DeclinedClassificationTest handleOkOnClick={handleOkOnClick} />
  }

  return (
    <Loading
      isLoading={
        isTestLoading || isDocumentLoading || isTestAnswersLoading || isTestAvailabilityLoading
      }
    >
      {testData && (
        <ProfessionalClassificationTestForm
          clientCategorizationPolicy={
            docs
              ?.find((doc) => doc.tickmillCompanyId === account?.tickmillCompany.id)
              ?.productCountryLegalDocuments.find(
                (countryDoc) => countryDoc.tickmillProductId === product
              )
              ?.legalDocuments.find((legalDoc) => legalDoc.code === 'client_categorisation_policy')
              ?.url ?? ''
          }
          handlePortfolioModalOpen={handlePortfolioModalOpen}
          handleTradeSizeModalOpen={handleTradeSizeModalOpen}
          testData={testData}
          handleFormSubmit={submitForm}
          isFormEnabled={!!isFormEnabled}
          testAnswers={testAnswers}
          classificationName={account?.classification.name}
        />
      )}
    </Loading>
  )
}

export default ProfessionalClassificationTest
